import React, { createContext, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "@/components/ConfirmDialog";

// Confirmación de borrado en bloque para las tablas con selección múltiple.
//
// Por qué existe: `window.confirm` pone la acción destructiva en un botón de
// "aceptar", lo enmarca con un "localhost dice" que la gente cierra sin leer, y no
// distingue borrar una fila de borrar treinta. Aquí sí: título rojo, el número
// delante y el foco puesto en Cancelar.
//
// Por qué un contexto y no estado en cada página: el diálogo se pinta UNA vez en
// AppLayout. Las nueve tablas solo piden la confirmación y esperan la respuesta,
// sin añadir estado ni JSX propio:
//
//   const confirmarBorrado = useConfirmDelete();
//   if (!(await confirmarBorrado(ids.length))) return;
const ConfirmDeleteContext = createContext(null);

export function ConfirmDeleteProvider({ children }) {
  const { t } = useTranslation();
  const [pendiente, setPendiente] = useState(null);   // { count }
  const resolveRef = useRef(null);

  const confirmar = (count) =>
    new Promise((resolve) => {
      resolveRef.current = resolve;
      setPendiente({ count });
    });

  const responder = (ok) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPendiente(null);
    if (resolve) resolve(ok);
  };

  return (
    <ConfirmDeleteContext.Provider value={confirmar}>
      {children}
      {pendiente && (
        <ConfirmDialog
          open
          onOpenChange={(v) => { if (!v) responder(false); }}
          title={t("common.cannotUndo")}
          intro={t("common.deleteSelectedIntro", { count: pendiente.count })}
          confirmLabel={t("common.confirmDeleteBtn")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => responder(true)}
        />
      )}
    </ConfirmDeleteContext.Provider>
  );
}

// Sin proveedor (una página montada fuera de AppLayout) se cae al confirm del
// navegador antes que dejar de preguntar: perder el aviso sería perder el freno.
export function useConfirmDelete() {
  const confirmar = useContext(ConfirmDeleteContext);
  const { t } = useTranslation();
  return confirmar || ((count) =>
    Promise.resolve(window.confirm(t("common.confirmDeleteSelected", { count }))));
}

export default useConfirmDelete;
